/**
 * PermissionsContext — loads the effective permission map from
 * /api/auth/me/effective-permissions and exposes helpers used to hide
 * sidebar entries and block route access.
 *
 * Usage:
 *   const { canAccessModule, canUseFeature, loading, perms } = usePerms();
 *   if (!canAccessModule('cases')) return <Forbidden />;
 */
import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { BACKEND_URL } from '../config/canonical';

// Iter120L: hard role-based hide list. These modules are ALWAYS hidden for
// the specified roles, regardless of what the backend effective-permission
// map returns. This is defense-in-depth against admin misconfiguration
// (i.e., forgetting to disable a module for the client role).
const HARD_ROLE_DENY = {
  client: new Set([
    'recruitment', 'training_center', 'clients', 'client_intakes',
    'analytics', 'team', 'gps_tracking', 'operations_map', 'skip_trace',
    'case_acceptance', 'field_operations', 'ai_investigation',
    'personnel_management', 'user_management', 'payroll', 'licensing',
    'internal_administration', 'company_administration',
  ]),
};

const PermissionsContext = createContext(null);

export const usePerms = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    // Never crash — return a permissive default so pages don't blank out
    // if the provider isn't mounted (e.g., on the login screen).
    return {
      loading: false,
      perms: null,
      canAccessModule: () => true,
      canUseFeature: () => true,
      refresh: () => {},
    };
  }
  return ctx;
};

export const PermissionsProvider = ({ children }) => {
  const { isAuthenticated, user, token } = useAuth();
  const [perms, setPerms] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setPerms(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me/effective-permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setPerms(await res.json());
      else setPerms(null);
    } catch (e) {
      console.error('Failed to load permissions', e);
      setPerms(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  useEffect(() => { load(); }, [load]);

  const canAccessModule = useCallback((moduleId) => {
    // Main hub role bypasses all checks
    if (user?.role === 'main_hub') return true;
    // Defense-in-depth: hard-deny list per role — always applies, cannot be
    // overridden by an admin miswiring the effective permission map.
    const denySet = HARD_ROLE_DENY[user?.role];
    if (denySet && denySet.has(moduleId)) return false;
    // If perms haven't loaded, be permissive to avoid a false "no access" flash
    if (!perms) return true;
    return perms.modules?.[moduleId] !== false;
  }, [perms, user]);

  const canUseFeature = useCallback((moduleId, featureId) => {
    if (user?.role === 'main_hub') return true;
    const denySet = HARD_ROLE_DENY[user?.role];
    if (denySet && denySet.has(moduleId)) return false;
    if (!perms) return true;
    if (perms.modules?.[moduleId] === false) return false;
    return perms.features?.[moduleId]?.[featureId] !== false;
  }, [perms, user]);

  const value = { loading, perms, canAccessModule, canUseFeature, refresh: load };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export default PermissionsContext;
